import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  ArrowRight, Phone, EnvelopeSimple, MapPin, X, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSiteContent } from "../i18n/SiteContent";

// Visually-hidden helper (screen-reader only) — silences Radix a11y warnings
const srOnly = {
  position: "absolute",
  width: 1, height: 1, padding: 0, margin: -1,
  overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s\-()]{6,}$/;

const empty = { name: "", email: "", phone: "", subject: "", message: "" };
const OPSYS_MAP_EMBED =
  "https://www.google.com/maps?q=Opsys+Trading,+Dubai&output=embed";
const OPSYS_MAP_LINK =
  "https://www.google.com/maps/place/Opsys+Trading/@25.033905,55.1886215,17z";

/** Simple 2-integer math captcha — no external service, keeps flow lightweight. */
function useMathCaptcha() {
  const [a, setA] = useState(0);
  const [b, setB] = useState(0);
  const refresh = () => {
    setA(Math.floor(Math.random() * 9) + 1);
    setB(Math.floor(Math.random() * 9) + 1);
  };
  useEffect(refresh, []);
  return { a, b, sum: a + b, refresh };
}

export default function ConnectDialog({ open, onOpenChange }) {
  const { settings, pg } = useSiteContent();
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [captchaVal, setCaptchaVal] = useState("");
  const captcha = useMathCaptcha();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const parseList = (raw) => {
    if (!raw) return [];
    const s = String(raw).trim();
    if (!s) return [];
    if (s.startsWith("[")) { try { const p = JSON.parse(s); if (Array.isArray(p)) return p.map((x) => String(x).trim()).filter(Boolean); } catch { /* ignore */ } }
    return s.split(",").map((x) => x.trim()).filter(Boolean);
  };
  const contactName = (settings?.contact_name || "").trim();
  const emails = Array.from(new Set([
    ...(settings?.email ? [settings.email] : []),
    ...(settings?.email_alt ? [settings.email_alt] : []),
    ...parseList(settings?.emails_extra),
  ])).filter(Boolean);
  const phones = Array.from(new Set([
    ...(settings?.phone ? [settings.phone] : []),
    ...parseList(settings?.phones_extra),
  ])).filter(Boolean);
  const address  = settings?.address   || "";
  const mapEmbed = settings?.map_embed || "";
  const mapLink  = settings?.map_url   || "";

  useEffect(() => {
    if (!open) return;
    // reset on open
    setForm(empty); setErrors({}); setCaptchaVal(""); captcha.refresh();
  }, [open]);

  const validate = () => {
    const e = {};
    if (!form.name.trim())               e.name    = "Full name is required";
    if (!EMAIL_RE.test(form.email))      e.email   = "Valid email required";
    if (!PHONE_RE.test(form.phone.trim())) e.phone = "Valid contact number required";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Please describe your requirements (10+ chars)";
    if (parseInt(captchaVal, 10) !== captcha.sum) e.captcha = "Captcha does not match";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — our team will reach out within one business day.");
      setForm(empty); setErrors({}); setCaptchaVal("");
      onOpenChange(false);
    } catch {
      toast.error("Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 overflow-hidden max-w-[1080px] w-[95vw] sm:w-[92vw] gap-0 bg-white rounded-2xl"
        data-testid="connect-dialog"
      >
        <DialogTitle style={srOnly}>Contact OpSys Digital</DialogTitle>
        <DialogDescription style={srOnly}>
          Share your project details and our team will reach out within one business day.
        </DialogDescription>
        <button
          type="button"
          aria-label="Close"
          onClick={() => onOpenChange(false)}
          data-testid="connect-close"
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-white/95 hover:bg-white text-[#0F2A6E] flex items-center justify-center shadow-sm border border-slate-200"
        >
          <X size={16} weight="bold" />
        </button>

        <div className="grid md:grid-cols-2">
          {/* LEFT: MAP + CONTACT DETAILS */}
          <div className="relative bg-[#0F2A6E] text-white p-8 md:p-9 flex flex-col">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-blue-200/80 mb-2">{pg("contact.dialog_eyebrow", "") || pg("contact.eyebrow", "Contact")}</div>
              <h3
                className="font-heading font-bold text-3xl md:text-[34px] tracking-tight leading-[1.05]"
                style={{ letterSpacing: "-0.025em" }}
                data-testid="connect-map-heading"
              >
                {pg("contact.dialog_heading", "") || pg("contact.heading", "Let's build something reliable together.")}
              </h3>
              <p className="mt-3 text-[13.5px] text-slate-200/85 leading-relaxed max-w-sm font-light">
                {pg("contact.dialog_sub", "")
                  || pg("contact.sub", "")
                  || (contactName
                    ? `Share a few details and ${contactName} will reach out within one business day.`
                    : "Share a few details and our team will reach out within one business day.")}
              </p>
            </div>

            {mapEmbed && (
              <div className="mt-6 rounded-xl overflow-hidden border border-white/10 relative flex-1 min-h-[240px] md:min-h-[300px]">
                <iframe
                  title="OpSys Trading — location"
                  src={mapEmbed}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full min-h-[240px] md:min-h-[300px] border-0"
                  style={{ filter: "grayscale(0.15) contrast(1.05)" }}
                  allowFullScreen
                  data-testid="connect-map-iframe"
                />
              </div>
            )}

            <ul className="mt-5 space-y-3 text-[13px]">
              {address && (
                <li className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                        style={{ background: "rgba(255,255,255,0.10)", color: "#8FB5FF" }}>
                    <MapPin size={14} weight="duotone" />
                  </span>
                  {mapLink ? (
                    <a href={mapLink} target="_blank" rel="noreferrer" className="text-slate-200/90 hover:text-white leading-snug pt-1">{address}</a>
                  ) : (
                    <span className="text-slate-200/90 leading-snug pt-1">{address}</span>
                  )}
                </li>
              )}
              {emails.length > 0 && (
                <li className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                        style={{ background: "rgba(255,255,255,0.10)", color: "#8FB5FF" }}>
                    <EnvelopeSimple size={14} weight="duotone" />
                  </span>
                  <div className="flex flex-col pt-1">
                    {emails.map((em) => (
                      <a key={em} href={`mailto:${em}`} className="text-slate-200/90 hover:text-white">{em}</a>
                    ))}
                  </div>
                </li>
              )}
              {phones.length > 0 && (
                <li className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                        style={{ background: "rgba(255,255,255,0.10)", color: "#8FB5FF" }}>
                    <Phone size={14} weight="duotone" />
                  </span>
                  <div className="flex flex-col pt-1">
                    {phones.map((ph) => (
                      <a key={ph} href={`tel:${ph.replace(/\s/g,"")}`} className="text-slate-200/90 hover:text-white">{ph}</a>
                    ))}
                  </div>
                </li>
              )}
            </ul>
          </div>

          {/* RIGHT: FORM */}
          <div className="p-8 md:p-9 bg-white">
            <h4 className="font-heading font-semibold text-xl md:text-2xl tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.02em" }}>
              {pg("contact.form_heading", "Share your requirements")}
            </h4>
            <p className="mt-1.5 text-[12.5px] text-[#6B7A8D]">{pg("contact.form_note", "All fields required unless noted.")}</p>

            <form onSubmit={submit} className="mt-5 space-y-3.5" noValidate>
              <div>
                <Label htmlFor="cd-name">Full name</Label>
                <Input id="cd-name" data-testid="connect-name" value={form.name}
                       onChange={(e) => set("name", e.target.value)} className="mt-1.5" />
                {errors.name && <div className="text-[12px] mt-1 text-red-500">{errors.name}</div>}
              </div>

              <div>
                <Label htmlFor="cd-email">Email ID</Label>
                <Input id="cd-email" type="email" data-testid="connect-email" value={form.email}
                       onChange={(e) => set("email", e.target.value)} className="mt-1.5" />
                {errors.email && <div className="text-[12px] mt-1 text-red-500">{errors.email}</div>}
              </div>

              <div>
                <Label htmlFor="cd-phone">Contact number</Label>
                <Input id="cd-phone" data-testid="connect-phone" value={form.phone}
                       onChange={(e) => set("phone", e.target.value)} placeholder="+971 5x xxx xxxx" className="mt-1.5" />
                {errors.phone && <div className="text-[12px] mt-1 text-red-500">{errors.phone}</div>}
              </div>

              <div>
                <Label htmlFor="cd-message">Describe your requirements</Label>
                <Textarea id="cd-message" data-testid="connect-message" rows={3} value={form.message}
                          onChange={(e) => set("message", e.target.value)} className="mt-1.5"
                          placeholder="Tell us about your project scope, timelines and any specific needs…" />
                {errors.message && <div className="text-[12px] mt-1 text-red-500">{errors.message}</div>}
              </div>

              {/* CAPTCHA */}
              <div>
                <Label htmlFor="cd-captcha">Captcha</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <div
                    className="h-10 px-4 rounded-md border border-slate-200 bg-slate-50 flex items-center gap-2 font-mono text-[14px] font-semibold select-none"
                    style={{ color: "#0F2A6E" }}
                    data-testid="connect-captcha-question"
                  >
                    <span>{captcha.a} + {captcha.b} = ?</span>
                    <button
                      type="button"
                      onClick={() => { captcha.refresh(); setCaptchaVal(""); }}
                      className="text-[#1B6FE0] hover:text-[#0F2A6E] transition"
                      aria-label="Refresh captcha"
                      data-testid="connect-captcha-refresh"
                    >
                      <ArrowsClockwise size={14} weight="bold" />
                    </button>
                  </div>
                  <Input
                    id="cd-captcha"
                    data-testid="connect-captcha-input"
                    value={captchaVal}
                    onChange={(e) => setCaptchaVal(e.target.value.replace(/[^\d-]/g, ""))}
                    className="w-24"
                    placeholder="Answer"
                    inputMode="numeric"
                  />
                </div>
                {errors.captcha && <div className="text-[12px] mt-1 text-red-500">{errors.captcha}</div>}
              </div>

              <div className="pt-1">
                <Button type="submit" disabled={submitting} data-testid="connect-submit"
                        className="w-full h-12 text-white font-semibold hover:brightness-110"
                        style={{ background: "#1B6FE0" }}>
                  {submitting ? "Sending…" : (<>Submit <ArrowRight size={14} weight="bold" /></>)}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
